// meldLayout — pure layout for a 副露 (called meld), encoding the standard display conventions as a
// flat cell list the renderer can draw. Separated from JSX so the conventions are unit-testable.
//
// Conventions:
//  - pon / 大明槓(minkan): the called tile is laid SIDEWAYS; its POSITION encodes the source —
//    上家(kamicha,left)=leftmost, 対面(toimen)=middle, 下家(shimocha,right)=rightmost (4-player rule).
//  - チー(chii): only ever from 上家, so the taken tile is rotated and placed leftmost; the rest ascend.
//  - 暗槓(ankan): the two END tiles face DOWN, the middle two face up; no rotation (concealed = no source).
//  - 加槓(shouminkan): like pon, plus the added 4th tile STACKED sideways on the rotated tile.
//  - 北抜き(kita): not stored as a meld (it's kitaCount) — handled by the caller, not here.
import { compareTiles } from '@mj/protocol';
import type { Meld, Seat, TileId } from '@mj/protocol';

export interface MeldCell {
  tile: TileId;
  faceDown?: boolean; // 暗槓 end tiles
  rotated?: boolean; // the called tile (横向き)
  stack?: TileId; // 加槓: the 4th tile stacked sideways on the rotated tile
}

// Which display slot carries the rotated tile, from the source's direction relative to the owner.
// 4-player convention; for 5/6p the position can't fully encode direction (no real table) — 'middle'
// is the graceful fallback (the caller can add a direction label there).
function calledSlot(calledFrom: Seat | null, ownerSeat: Seat, players: number): 'left' | 'middle' | 'right' {
  if (calledFrom == null) return 'right';
  const pos = (((calledFrom - ownerSeat) % players) + players) % players;
  if (pos === players - 1) return 'left'; // 上家
  if (pos === 1) return 'right'; // 下家
  return 'middle'; // 対面 / その他
}

export function meldLayout(meld: Meld, ownerSeat: Seat, players: number): MeldCell[] {
  const { kind, tiles, calledFrom, calledTileIndex } = meld;

  if (kind === 'ankan') {
    return tiles.map((t, i) => ({ tile: t, faceDown: i === 0 || i === tiles.length - 1 }));
  }
  if (kind === 'kita') {
    return tiles.map((t) => ({ tile: t }));
  }

  if (kind === 'chii') {
    const called = tiles[calledTileIndex] ?? tiles[0]!;
    const rest = tiles.filter((_, i) => i !== calledTileIndex).slice().sort(compareTiles);
    return [{ tile: called, rotated: true }, ...rest.map((t) => ({ tile: t }))];
  }

  // pon(3) / 大明槓(4) / 加槓(3 + stacked 4th)
  const slot = calledSlot(calledFrom, ownerSeat, players);
  const base = kind === 'minkan' ? 4 : 3;
  const t = tiles[0]!;
  const rotIndex = slot === 'left' ? 0 : slot === 'right' ? base - 1 : Math.min(1, base - 1);
  const cells: MeldCell[] = [];
  for (let i = 0; i < base; i++) cells.push({ tile: t, rotated: i === rotIndex });
  if (kind === 'shouminkan') cells[rotIndex]!.stack = tiles[3] ?? t;
  return cells;
}
